// =============================================================================
// ui/state/tradeFeed.tsx — ONE poller, so a pushed trade shows up (TCR-95).
// -----------------------------------------------------------------------------
// Josh pushes trades from ATAS into a tab that is already open, and until this
// existed they did not appear until he reloaded. Beta has always picked them up.
//
// ── 🎯 BETA POLLS. IT DOES NOT SUBSCRIBE. ──
//
// Beta's own comment calls this "realtime trade sync", which is misleading
// enough to be worth repeating here: there is no `postgres_changes` channel
// behind it. It is `setInterval(…, 15000)` guarded on `visibilityState`, plus a
// second trigger on `visibilitychange`. Building a subscription instead would be
// inventing a capability beta never had, and the parity gate measures against
// beta (D7).
//
// ⚠ THE FOCUS TRIGGER IS NOT DECORATION. Beta's reason, verbatim: "Supabase
// sessions persist for ~1 year, so a user can keep TRADECOACH open in a
// background tab all week without ever triggering boot()." That is Josh's actual
// usage, and the 15s timer never fires while the tab is hidden — so without the
// focus trigger, coming back to a week-old tab would show a week-old book until
// the next tick.
//
// ── 🔴 WHY ONE POLLER AND NOT ELEVEN ──
//
// `useTradeRows` is a per-consumer hook, and ELEVEN files call it: Analytics,
// AnalyticsCustom, AnalyticsExcursion, AnalyticsSetup, Backtests, Dashboard,
// DayOverview, Journal, Patterns, Review, Trades. Eleven tabs each starting
// their own 15s timer is not a port of beta, it is a stampede — and the ones
// that are mounted at any moment depend on which tab you are looking at, so the
// request rate would be a function of navigation.
//
// So: this provider owns the single timer and a single nonce, and `useTradeRows`
// reads the nonce out of this context and adds it to its own effect deps. No
// call site changes, and the request count does not depend on how many tabs are
// on screen.
//
// ── 🚨 THE RULE THAT KEEPS THIS FROM UNDOING TCR-79, TCR-80 AND TCR-91 ──
//
//   A CYCLE CHANGES WHAT THE LISTS AND FIGURES QUERY. IT NEVER TOUCHES WHAT AN
//   OPEN EDITOR HOLDS.
//
// Those three cards all landed in the last month and all of them are about typed
// text surviving: the Journal commits on blur, the four record modals hold a
// draft above the tab, and the annotation overlay freezes its field list when it
// opens. A poll that re-seeded a form mid-edit would undo every one of them.
//
// ✅ MEASURED, so the rule is close to free rather than fragile: `TradeDetail`
// loads its own row in its own effect keyed on `tradeId`, and both call sites
// open it from local state (`editing` in `Trades.tsx`, `openId` in
// `DayOverview.tsx`) — never from `useTradeRows`. The Journal's `dayRows` is
// used only to pick out the trades for a date; it does not seed the editor. So
// bumping the nonce re-reads lists and leaves drafts alone. ▶ Keep it that way:
// anything added here must stay on the query side of that line.
//
// ── ⚠ THE ONE PLACE THIS DISAGREES WITH EVERY OTHER PROVIDER HERE ──
//
// `useContracts`, `useAccounts` and the rest THROW outside their provider, on
// purpose — a screen reading an empty catalogue is worse than a screen that
// crashed. This one must NOT. `useTradeRows` is rendered by a large number of
// existing tests that mount a tab with no shell around it, and a throwing
// context would turn a feature nobody asked those tests about into a few hundred
// failures. So the default is a nonce of 0 and a no-op poller, which is exactly
// "nothing new ever arrives" — the behaviour those tests already assert.
// =============================================================================

import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { loadTradeIds } from '../../io/sync/trades';
import { reportFailure } from '../../io/diagnostics/errorLog';

/** Beta's interval, to the millisecond (`app/index.html`). */
export const TRADE_FEED_POLL_MS = 15_000;

export interface TradeFeedState {
  /**
   * Bumped when the set of trade ids on the server has changed since the last
   * cycle. ⚠ It is a SIGNAL, not a count of anything — the only meaningful
   * thing to do with it is put it in an effect's dependencies.
   */
  nonce: number;
  /**
   * Run a cycle now, if one is not already running.
   *
   * ⚠ Nothing in the app calls this yet, and that is deliberate: a local write
   * already has `reloadNonce`, which is both faster and certain. This is here
   * for the tests and for a future surface that wants to say "look again now"
   * about a write it did not make.
   */
  check: () => void;
}

/**
 * ⚠ DEFAULTS, NOT NULL — see the header. Outside a provider the nonce never
 * moves and `check()` does nothing, which is the pre-TCR-95 behaviour.
 */
const Ctx = createContext<TradeFeedState>({ nonce: 0, check: () => {} });

export function useTradeFeed(): TradeFeedState {
  return useContext(Ctx);
}

export function TradeFeedProvider(
  { sb, userId, children }:
  { sb: SupabaseClient | null; userId: string | null; children: ReactNode },
) {
  const [nonce, setNonce] = useState(0);

  // ⚠ A REF, NOT STATE. Writing the seen set to state would re-render every
  // consumer on every cycle — including the cycles that found nothing, which is
  // all of them almost all of the time. The set is bookkeeping for the next
  // comparison; only the nonce is anybody's business.
  const seen = useRef<ReadonlySet<string> | null>(null);
  // ⚠ Guards against overlapping cycles. The timer and the focus trigger can
  // fire together on a slow network, and two probes racing would compare the
  // second result against a set the first had already replaced.
  const busy = useRef(false);

  const check = useCallback(() => {
    if (!sb || !userId || busy.current) return;
    busy.current = true;

    void (async () => {
      try {
        const ids = await loadTradeIds(sb);
        const before = seen.current;
        seen.current = ids;

        // 🔴 THE FIRST CYCLE SEEDS AND DOES NOT BUMP. Treating "I had no set
        // and now I have 700 ids" as a change would fire a full re-read on
        // every boot, immediately after the read that just populated the
        // screen — a doubled first paint for no new information.
        if (!before) return;

        // Size first because it is O(1) and it is the case that actually
        // happens; the membership walk is for the insert-plus-delete window
        // that leaves the count where it was.
        const changed = ids.size !== before.size
          || [...ids].some((id) => !before.has(id));
        if (changed) setNonce((n) => n + 1);
      } catch (e) {
        // ⚠ REPORTED AND SWALLOWED. A failed probe must not stop the timer: the
        // overwhelmingly likely cause is a dropped connection, and a poller
        // that gives up on the first blip is one that has stopped working by
        // the time anyone would notice. `seen` keeps whatever it had, so the
        // next good cycle compares against a real set rather than re-seeding.
        reportFailure('trade feed probe', e);
      } finally {
        busy.current = false;
      }
    })();
  }, [sb, userId]);

  useEffect(() => {
    if (!sb || !userId) return;

    // ⚠ `document` is read inside the handlers rather than captured, because
    // the whole question is what the visibility is AT THE MOMENT THE TIMER
    // FIRES.
    const visible = () => document.visibilityState === 'visible';

    // Seed immediately when mounted on a visible tab. Without this the first
    // comparison set is 15 seconds old, so a trade pushed in those 15 seconds
    // reads as "already seen" and is never noticed until the one after it.
    if (visible()) check();

    const timer = setInterval(() => { if (visible()) check(); }, TRADE_FEED_POLL_MS);

    const onVisibility = () => { if (visible()) check(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [sb, userId, check]);

  // ⚠ NOT memoised on purpose. `nonce` changes are the only renders this
  // provider causes, and on a nonce change every consumer has to run anyway —
  // a stable object identity would buy nothing and hide that.
  return <Ctx.Provider value={{ nonce, check }}>{children}</Ctx.Provider>;
}
